/**
 * A count, its label, and an optional proportional bar.
 *
 * The bar is measured against a SET-level domain handed down from the set, not
 * against the row's own value, because "1,240 citations" only means something
 * beside the other rows' citation counts. When the domain is absent the figure
 * still renders and the bar does not.
 *
 * With no value there is no bar and no track: an empty track reads as a
 * measured zero, which is a different claim from "this lane did not carry it".
 *
 * The bar grows from the LEADING edge, which is not the left one. `scaleX` needs
 * a `transform-origin` and CSS has no logical keyword for it, so the kit states
 * both directions once in `.cresultcard__fill` (`left center`, and `right
 * center` under `:dir(rtl)`). An inline `transformOrigin` here outranked both
 * and made the right-to-left rule dead code, so a Hebrew or Arabic surface grew
 * its bars from the END of their own line. The origin is the stylesheet's, and
 * the stylesheet is imported here rather than assumed from a sibling, because
 * without it the origin falls back to the element's centre.
 */
"use client";
import type { ReactElement } from "react";
import { formatCount } from "./cresultcard.format";
import {
  CRC_NUMERIC_STYLE,
  CRC_TEXT,
  CRC_TEXT_FAINT,
  CRC_TONE_COLOR,
} from "./cresultcard.tokens";
import { CresultcardTone } from "./cresultcard.types";
import "./Cresultcard.css";

const TRACK_HEIGHT = 3;
const TRACK_RADIUS = 2;

interface CresultcardMagnitudeProps {
  readonly value?: number;
  readonly label: string;
  /** The largest value in the set, so every bar shares one scale. */
  readonly domain?: number;
  readonly tone?: CresultcardTone;
}

export function CresultcardMagnitude({
  value,
  label,
  domain,
  tone = CresultcardTone.Positive,
}: CresultcardMagnitudeProps): ReactElement | null {
  if (value == null) return null;

  const fraction =
    domain == null || domain <= 0 ? null : Math.min(1, value / domain);

  return (
    <div
      data-el="Cresultcard/Magnitude/Root"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        minWidth: 0,
      }}
    >
      <span
        style={{
          ...CRC_NUMERIC_STYLE,
          display: "flex",
          alignItems: "baseline",
          gap: 6,
          minWidth: 0,
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 740,
            letterSpacing: "-0.02em",
            lineHeight: 1,
            color: CRC_TEXT,
          }}
        >
          {formatCount(value)}
        </span>
        <span
          style={{
            fontSize: 9.5,
            fontWeight: 560,
            lineHeight: 1.2,
            color: CRC_TEXT_FAINT,
          }}
        >
          {label}
        </span>
      </span>
      {fraction != null && (
        <span
          aria-hidden
          data-el="Cresultcard/Magnitude/Track"
          style={{
            position: "relative",
            height: TRACK_HEIGHT,
            overflow: "hidden",
            borderRadius: TRACK_RADIUS,
            backgroundColor: "rgba(255,255,255,0.07)",
          }}
        >
          <span
            className="cresultcard__fill"
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: TRACK_RADIUS,
              opacity: 0.85,
              backgroundColor: CRC_TONE_COLOR[tone],
              transform: `scaleX(${String(fraction)})`,
            }}
          />
        </span>
      )}
    </div>
  );
}
